import { type CandidateInfo } from "@/lib/askAiUtils";
import { Mail, Calendar, ExternalLink, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

interface CandidateInfoDisplayProps {
  candidateInfo: CandidateInfo[];
}

export function CandidateInfoDisplay({ candidateInfo }: CandidateInfoDisplayProps) {
  const [copiedEmails, setCopiedEmails] = useState<Set<string>>(new Set());
  const copyEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopiedEmails(prev => new Set(prev).add(email));
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedEmails(prev => {
          const newSet = new Set(prev);
          newSet.delete(email);
          return newSet;
        });
      }, 2000);
    } catch (error) {
      console.error('Failed to copy email:', error);
    }
  };

  if (!candidateInfo || candidateInfo.length === 0) return null;
  
  return (
    <div className="mt-4 space-y-3">
      <div className="text-sm font-medium text-gray-700 space-grotesk">
        Candidate Details:
      </div>
      {candidateInfo.map((info, index) => (
        <div key={index} className="bg-white border rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-900 space-grotesk">
              {/* Use firstName/lastName from response as fallback */}
              {info.candidate 
                ? `${info.candidate.firstName} ${info.candidate.lastName}` 
                : (info.firstName && info.lastName) 
                  ? `${info.firstName} ${info.lastName}`
                  : `Candidate ${info.key}`}
            </div>
            <div className={`text-xs px-2 py-1 rounded space-grotesk ${
              info.matchScore > 0 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-600'
            }`}>
              {info.matchScore > 0 ? `${Math.round(info.matchScore * 100)}% match` : 'Score: 0'}
            </div>
          </div>
          {/* Show info from candidate OR from direct response fields */}
          {(info.candidate || info.firstName) && (
            <div className="space-y-2 text-xs text-gray-600 space-grotesk">
              {/* Email with copy functionality */}
              {info.candidate?.email && (
                <div className="flex items-center gap-2">
                  <Mail className="h-3 w-3 flex-shrink-0" />
                  <span className="flex-1">{info.candidate.email}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyEmail(info.candidate!.email)}
                    className="h-6 w-6 p-0 hover:bg-gray-100"
                    title="Copy email"
                  >
                    {copiedEmails.has(info.candidate.email) ? (
                      <Check className="h-3 w-3 text-green-600" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                  </Button>
                </div>
              )}
              
              {/* Bio */}
              {info.bio && (
                <div className="bg-gray-50 rounded-md p-3 mt-3">
                  <div className="text-xs font-medium text-gray-700 mb-1 space-grotesk">Bio</div>
                  <div className="text-xs text-gray-600 leading-relaxed space-grotesk">
                    {info.bio}
                  </div>
                </div>
              )}
              
              {/* Resume - check candidate first, then direct resumeUrl */}
              {(info.candidate?.resumeUrl || info.resumeUrl) && (
                <div className="flex items-center gap-2">
                  <ExternalLink className="h-3 w-3 flex-shrink-0" />
                  <a 
                    href={info.candidate?.resumeUrl || info.resumeUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-600 hover:underline"
                  >
                    {info.candidate?.resumeFileName || 'Resume'}
                  </a>
                </div>
              )}
              
              {/* Resumes array */}
              {info.candidate?.resumes && info.candidate.resumes.length > 0 && (
                <div className="space-y-1">
                  {info.candidate.resumes.map((resume, resumeIndex) => (
                    <div key={resumeIndex} className="flex items-center gap-2">
                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      <a 
                        href={resume.url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-blue-600 hover:underline"
                      >
                        {resume.fileName || `Resume ${resumeIndex + 1}`}
                      </a>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Experience */}
              {info.candidate?.experienceYears && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-3 w-3 flex-shrink-0" />
                  <span>Experience: {info.candidate.experienceYears} years</span>
                </div>
              )}
              
              {/* LinkedIn - check candidate first, then direct linkedIn */}
              {(info.candidate?.linkedinUrl || info.linkedIn) && (
                <div className="flex items-center gap-2">
                  <ExternalLink className="h-3 w-3 flex-shrink-0" />
                  <a 
                    href={info.candidate?.linkedinUrl || info.linkedIn} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-600 hover:underline"
                  >
                    LinkedIn Profile
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
